import React, { useState } from 'react';
import { LogoIcon } from '../components/Logo';

const GREEN = '#0f6e56';
const DARK = '#0a3d2e';
const TEAL = '#1d9e75';
const MINT = '#9fe1cb';
const MIST = '#e1f5ee';
const INK = '#1a2b25';
const MUT = '#5b6b64';

// Vibrant, professional accent system — cohesive with the forest-green logo
const BLUE = '#2563eb', BLUE_SOFT = '#eaf1ff';
const VIOLET = '#7c3aed', VIOLET_SOFT = '#f1ebff';

const VIDEOS = [
  { title: 'How to create an invoice', desc: 'See how easy it is to bill a customer', embed: 'https://app.trupeer.ai/embed/HYmGYQGXq' },
  { title: 'Financial Dashboard & Analytics', desc: 'Understand your numbers at a glance', embed: 'https://app.trupeer.ai/embed/NwaEoIxYF' },
];

const INDUSTRIES = {
  tourism: {
    key: 'tourism', label: 'Travel & Tourism', short: 'Travel', accent: GREEN, soft: MIST, icon: '✈️',
    heroTitle: 'Accounting built for travel agencies',
    heroSub: 'Issue tickets, track PNRs, settle with airlines, and see your margin on every booking — with proper books kept automatically.',
    blurb: 'Purpose-built for travel agencies and tour operators. Capture booking details on every invoice, import your Daily Sales Report, and know your profit on each ticket.',
    benefits: [
      ['Booking-aware invoices', 'Capture PNR, passenger, sector, airline and ticket number on every invoice.'],
      ['Buy & sell in one step', 'Import your Daily Sales Report and each row books the client sale and the airline purchase together.'],
      ['Multi-currency & forex', 'Bill in any currency; foreign settlements post the exchange gain or loss for you.'],
      ['Margin at a glance', 'Client total minus supplier cost — your profit on every booking, and across the day.'],
    ],
    example: { billTo: 'Gulf Co', strip: '✈ PNR ABC123 · DOH-DXB-DOH · Qatar Airways', lines: [['Air ticket', '$550'], ['Less: discount', '-$20'], ['Add: tax', '+$28']], net: '$558' },
  },
  service: {
    key: 'service', label: 'Service Business', short: 'Service', accent: BLUE, soft: BLUE_SOFT, icon: '🛠️',
    heroTitle: 'Accounting for service businesses',
    heroSub: 'Agencies, consultants, salons and professionals — bill by job, hours or retainer, and keep clean books without the jargon.',
    blurb: 'For agencies, consultants, salons and professionals. Bill by job, hours or retainer, track who did the work, and see what each engagement earns.',
    benefits: [
      ['Job & project billing', 'Track job name, consultant, hours and rate — totals calculate as you type.'],
      ['Retainers & part payments', 'Bill-to-bill settlement handles partial payments and running balances cleanly.'],
      ['Multi-currency clients', 'Work with overseas clients and let forex gain or loss post automatically.'],
      ['Profit by client', 'See what each engagement earns with automatic profit and loss.'],
    ],
    example: { billTo: 'Acme Ltd', strip: '🛠 Website redesign · Sara · 12 hrs', lines: [['Design (12 hrs × $50)', '$600'], ['Less: discount', '-$40'], ['Add: tax', '+$28']], net: '$588' },
  },
  general: {
    key: 'general', label: 'General Business', short: 'Business', accent: VIOLET, soft: VIOLET_SOFT, icon: '📒',
    heroTitle: 'Simple accounting for everyday business',
    heroSub: 'Shops, traders and small businesses — record sales and expenses, and get real books, reports and tax summaries with zero fuss.',
    blurb: 'For shops, traders and everyday small businesses. Invoice by item and quantity or just an amount, track stock-in and stock-out, and keep clean books.',
    benefits: [
      ['Item or simple billing', 'Invoice by item and quantity, or just enter an amount — your choice.'],
      ['Receivables & payables', 'Know who owes you and who you owe, with aging built in.'],
      ['Real financial reports', 'Trial balance, profit and loss, and balance sheet — always up to date.'],
      ['Free plan to start', 'Run your books free, then upgrade to Pro when you need more power.'],
    ],
    example: { billTo: 'Walk-in Customer', strip: '📦 Rice bag 25kg × 10 · Sugar 5kg × 6', lines: [['Rice bag (10 × $40)', '$400'], ['Sugar (6 × $8)', '$48'], ['Add: tax', '+$22']], net: '$470' },
  },
};

export default function Landing({ onGetStarted, onTools, onLogin }) {
  const [pg, setPg] = useState('home');
  const go = (p) => { setPg(p); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  return (
    <div style={{ background: '#fff', color: INK, fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif' }}>
      <StyleTag />
      <Header go={go} pg={pg} onGetStarted={onGetStarted} onLogin={onLogin} />
      {pg === 'home' && <Home go={go} onGetStarted={onGetStarted} onTools={onTools} />}
      {INDUSTRIES[pg] && <IndustryPage cfg={INDUSTRIES[pg]} onGetStarted={onGetStarted} go={go} />}
      {pg === 'pricing' && <Pricing onGetStarted={onGetStarted} />}
      {pg === 'about' && <About onGetStarted={onGetStarted} />}
      <Footer go={go} onGetStarted={onGetStarted} />
    </div>
  );
}

function Header({ go, pg, onGetStarted, onLogin }) {
  const link = (label, p) => (
    <button onClick={() => go(p)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: pg === p ? GREEN : MUT, fontWeight: pg === p ? 600 : 500, padding: '6px 2px' }}>{label}</button>
  );
  return (
    <div style={{ position: 'sticky', top: 0, zIndex: 50, background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(10px)', borderBottom: '1px solid #eef1f0', padding: '0 clamp(16px,5vw,40px)', height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <button onClick={() => go('home')} style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'none', border: 'none', cursor: 'pointer' }}>
        <LogoIcon size={34} />
        <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1 }}>
          <span style={{ fontSize: 19, fontWeight: 700, color: GREEN, fontFamily: 'Georgia, serif' }}>FinlyBooks</span>
          <span style={{ fontSize: 9.5, fontWeight: 600, color: TEAL, letterSpacing: '0.06em', marginTop: 3 }}>TRACK. MANAGE. GROW.</span>
        </span>
      </button>
      <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 14 }} className="fb-navlinks">
          {link('Travel', 'tourism')}{link('Service', 'service')}{link('Business', 'general')}{link('Pricing', 'pricing')}{link('About', 'about')}
        </div>
        <button onClick={onLogin} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: INK, fontWeight: 500 }}>Sign in</button>
        <button onClick={onGetStarted} className="fb-lift" style={btnPrimary}>Start free</button>
      </div>
    </div>
  );
}

function Section({ children, bg = '#fff', pad = '72px clamp(16px,5vw,40px)' }) {
  return <div style={{ background: bg, padding: pad }}><div style={{ maxWidth: 1080, margin: '0 auto' }}>{children}</div></div>;
}

function Home({ go, onGetStarted, onTools }) {
  return (
    <>
      {/* HERO */}
      <div style={{ background: `radial-gradient(1200px 500px at 70% -10%, #14805f 0%, ${DARK} 55%)`, color: '#fff', padding: '56px clamp(16px,5vw,40px) 0' }}>
        <div style={{ maxWidth: 1080, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.05fr) minmax(0,0.95fr)', gap: 40, alignItems: 'center' }} className="fb-hero">
            <div>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'rgba(255,255,255,0.1)', color: MINT, fontSize: 13, fontWeight: 600, padding: '6px 14px', borderRadius: 20, border: '1px solid rgba(255,255,255,0.15)' }}><Dot /> Start free · Web &amp; mobile</span>
              <h1 style={{ fontSize: 'clamp(30px,5vw,48px)', lineHeight: 1.1, fontWeight: 800, margin: '18px 0 0', letterSpacing: '-0.02em' }}>Accounting that runs itself for small business</h1>
              <p style={{ fontSize: 18, color: MINT, lineHeight: 1.6, margin: '18px 0 0', maxWidth: 460 }}>Invoices, ledgers and real financial reports — simple enough for anyone, with proper double-entry kept quietly in the background.</p>
              <div style={{ display: 'flex', gap: 12, marginTop: 26, flexWrap: 'wrap' }}>
                <button onClick={onGetStarted} className="fb-lift" style={{ background: '#fff', color: DARK, border: 'none', padding: '14px 28px', borderRadius: 10, cursor: 'pointer', fontSize: 16, fontWeight: 700 }}>Start free →</button>
                <button onClick={onTools} className="fb-lift" style={{ background: 'rgba(255,255,255,0.12)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', padding: '14px 28px', borderRadius: 10, cursor: 'pointer', fontSize: 16, fontWeight: 600 }}>Try free invoice tool</button>
              </div>
              <div style={{ fontSize: 13, color: MINT, marginTop: 16, opacity: 0.85 }}>No credit card · Set up in minutes</div>
            </div>
            <HeroMock />
          </div>
          <div style={{ display: 'flex', gap: 26, justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', padding: '26px 0 22px', marginTop: 30, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            <span style={{ fontSize: 12, color: MINT, opacity: 0.7 }}>Built for</span>
            {['Travel agencies', 'Service firms', 'Retail & trade', 'Accountants'].map(t => <span key={t} style={{ fontSize: 14, color: '#fff', fontWeight: 500, opacity: 0.9 }}>{t}</span>)}
          </div>
        </div>
      </div>

      {/* STATS BAND */}
      <Section bg={GREEN} pad="26px clamp(16px,5vw,40px)">
        <div style={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: 14, textAlign: 'center', color: '#fff' }}>
          {[['3', 'industry editions'], ['$0', 'to get started'], ['2', 'platforms — web & Android'], ['100%', 'double-entry, always balanced']].map(([n, l]) => (
            <div key={l}><div style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.02em' }}>{n}</div><div style={{ fontSize: 13, color: MINT }}>{l}</div></div>
          ))}
        </div>
      </Section>

      {/* EDITION SELECTOR — interactive, with live example per edition */}
      <Section>
        <div style={{ textAlign: 'center', maxWidth: 640, margin: '0 auto' }}>
          <Kicker center>Choose your edition</Kicker>
          <h2 style={{ ...h2, textAlign: 'center' }}>One app, three tailored experiences</h2>
          <p style={{ fontSize: 16, color: MUT, marginTop: 10, lineHeight: 1.6 }}>FinlyBooks adapts to your business. Pick an edition to see how it works — you can switch anytime inside the app.</p>
        </div>
        <EditionSelector go={go} onGetStarted={onGetStarted} />
      </Section>

      {/* FEATURE ROWS */}
      <Section bg="#fafbfb">
        <Kicker>Everything you need</Kicker>
        <h2 style={h2}>Serious accounting, without the jargon</h2>
        <div style={{ marginTop: 36, display: 'flex', flexDirection: 'column', gap: 20 }}>
          <FeatureRow accent={GREEN} title="Bill in seconds" text="Create and share professional invoices — travel, service or retail — with discount, tax and your own logo. Send as a PDF over WhatsApp in a tap." bullets={['Industry-specific fields', 'Discount & tax built in', 'Your logo on every invoice']} visual={<InvoiceMock accent={GREEN} ex={INDUSTRIES.tourism.example} />} />
          <FeatureRow reverse accent={BLUE} title="See your profit, live" text="Every entry posts proper double-entry behind the scenes, so your profit & loss, balance sheet and aging are always up to date — never stale." bullets={['P&L and balance sheet', 'Receivables & payables aging', 'Trial balance that always balances']} visual={<ChartMock />} />
          <FeatureRow accent={VIOLET} title="Get paid, track every bill" text="Match payments to specific invoices and bills with bill-to-bill settlement. Partial payments and running balances are handled cleanly." bullets={['Bill-to-bill matching', 'Partial payments', 'Multi-currency & forex']} visual={<PayMock />} />
        </div>
      </Section>

      {/* COMPARISON */}
      <Section>
        <Kicker center>Why FinlyBooks</Kicker>
        <h2 style={{ ...h2, textAlign: 'center' }}>Simpler than the big names, and free to start</h2>
        <div style={{ overflowX: 'auto', marginTop: 30 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 520 }}>
            <thead><tr><th style={cmpTh}></th><th style={{ ...cmpTh, color: GREEN, fontWeight: 800 }}>FinlyBooks</th><th style={cmpTh}>Tally / Legacy</th><th style={cmpTh}>QuickBooks / Zoho</th></tr></thead>
            <tbody>
              {[['Real free plan', true, false, false], ['No accounting jargon', true, false, false], ['Industry editions built in', true, false, false], ['Multi-currency & forex', true, true, true], ['Export to Tally & ERPs', true, true, true], ['Works on mobile', true, false, true]].map(([label, a, b, c], i) => (
                <tr key={i} style={{ background: i % 2 ? '#fafbfb' : '#fff' }}>
                  <td style={{ ...cmpTd, textAlign: 'left', fontWeight: 500 }}>{label}</td>
                  <td style={cmpTd}>{a ? <Tick /> : <Dash />}</td><td style={cmpTd}>{b ? <Tick muted /> : <Dash />}</td><td style={cmpTd}>{c ? <Tick muted /> : <Dash />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* HOW IT WORKS */}
      <Section bg="#fafbfb">
        <Kicker>How it works</Kicker>
        <h2 style={h2}>Up and running in three steps</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 22, marginTop: 32 }}>
          {[['1', 'Set up your company', 'Add your logo, currency and pick your industry edition.', GREEN], ['2', 'Record your first sale', 'Create an invoice — the double-entry is posted behind the scenes.', BLUE], ['3', 'Watch your reports', 'Dashboards, P&L and balance sheet update automatically.', VIOLET]].map(([n, t, d, c]) => (
            <div key={n} style={{ display: 'flex', gap: 14 }}>
              <div style={{ flexShrink: 0, width: 34, height: 34, borderRadius: '50%', background: c, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>{n}</div>
              <div><div style={{ fontWeight: 700, fontSize: 16 }}>{t}</div><div style={{ fontSize: 14, color: MUT, lineHeight: 1.55, marginTop: 4 }}>{d}</div></div>
            </div>
          ))}
        </div>
      </Section>

      {/* DEMO VIDEOS */}
      <Section>
        <Kicker>See it in action</Kicker>
        <h2 style={h2}>Watch a quick demo</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))', gap: 22, marginTop: 32 }}>
          {VIDEOS.map(v => (
            <div key={v.title} className="fb-card" style={{ background: '#fff', border: '1px solid #e9ecec', borderRadius: 14, overflow: 'hidden' }}>
              <div style={{ position: 'relative', paddingTop: '56.25%', background: '#000' }}>
                <iframe title={v.title} src={v.embed} allow="fullscreen" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }} />
              </div>
              <div style={{ padding: 16 }}><div style={{ fontWeight: 700, fontSize: 15 }}>{v.title}</div><div style={{ fontSize: 13, color: MUT, marginTop: 3 }}>{v.desc}</div></div>
            </div>
          ))}
        </div>
      </Section>

      {/* FAQ */}
      <Section bg="#fafbfb">
        <Kicker>Questions</Kicker>
        <h2 style={h2}>Frequently asked</h2>
        <div style={{ marginTop: 28, maxWidth: 760 }}>
          {[
            ['Is FinlyBooks really free?', 'Yes — there is a real free plan covering invoices, ledgers, reports, receipts and payments for one business. Pro and Enterprise plans add advanced tools like multi-currency, tax modules, bulk upload and multi-user access.'],
            ['Can I export my data to Tally or another ERP?', 'Absolutely. You can download a full JSON backup, a Tally-ready XML (ledgers and vouchers), and universal CSV files including a complete daybook — your data is always yours.'],
            ['Do I need accounting knowledge to use it?', 'No. You just record sales, expenses and payments in plain language; FinlyBooks keeps the double-entry books correctly in the background.'],
            ['Does it work on mobile?', 'Yes — there is a web app and an Android app, so you can bill and check your numbers from anywhere.'],
            ['Which businesses is it for?', 'Travel agencies, service businesses and everyday shops and traders — each with a tailored edition you can switch anytime.'],
          ].map(([q, a], i) => <FAQItem key={i} q={q} a={a} />)}
        </div>
      </Section>

      <CTABand onGetStarted={onGetStarted} />
    </>
  );
}

// ---- Interactive edition selector ----
function EditionSelector({ go, onGetStarted }) {
  const [sel, setSel] = useState('tourism');
  const cfg = INDUSTRIES[sel];
  return (
    <div style={{ marginTop: 30 }}>
      {/* tabs */}
      <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
        {Object.values(INDUSTRIES).map(ind => {
          const on = sel === ind.key;
          return (
            <button key={ind.key} onClick={() => setSel(ind.key)} className="fb-lift" style={{
              display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer', padding: '11px 20px', borderRadius: 30, fontSize: 15, fontWeight: 600,
              border: `1.5px solid ${on ? ind.accent : '#e2e6e4'}`, background: on ? ind.accent : '#fff', color: on ? '#fff' : INK,
            }}>
              <span style={{ fontSize: 18 }}>{ind.icon}</span>{ind.short}
            </button>
          );
        })}
      </div>
      {/* panel */}
      <div className="fb-editionpanel" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 28, alignItems: 'center', marginTop: 28, background: cfg.soft, borderRadius: 20, padding: 'clamp(20px,4vw,36px)', border: `1px solid ${cfg.accent}22` }}>
        <div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#fff', color: cfg.accent, fontSize: 13, fontWeight: 700, padding: '6px 14px', borderRadius: 20, border: `1px solid ${cfg.accent}33` }}>{cfg.icon} {cfg.label} edition</div>
          <h3 style={{ fontSize: 'clamp(20px,3vw,26px)', fontWeight: 800, margin: '14px 0 0', letterSpacing: '-0.01em' }}>{cfg.heroTitle}</h3>
          <p style={{ fontSize: 15, color: MUT, lineHeight: 1.6, marginTop: 10 }}>{cfg.blurb}</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginTop: 16 }}>
            {cfg.benefits.slice(0, 3).map(([t]) => <div key={t} style={{ display: 'flex', gap: 9, fontSize: 14.5, alignItems: 'center' }}><Tick small color={cfg.accent} /> <span style={{ fontWeight: 500 }}>{t}</span></div>)}
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 22, flexWrap: 'wrap' }}>
            <button onClick={onGetStarted} className="fb-lift" style={{ background: cfg.accent, color: '#fff', border: 'none', padding: '11px 22px', borderRadius: 9, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>Start free →</button>
            <button onClick={() => go(cfg.key)} className="fb-lift" style={{ background: '#fff', color: cfg.accent, border: `1px solid ${cfg.accent}55`, padding: '11px 22px', borderRadius: 9, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>Explore {cfg.short} →</button>
          </div>
        </div>
        <div><InvoiceMock accent={cfg.accent} ex={cfg.example} /></div>
      </div>
    </div>
  );
}

function FeatureRow({ title, text, bullets, visual, reverse, accent = GREEN }) {
  return (
    <div className="fb-frow" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 28, alignItems: 'center' }}>
      <div style={{ order: reverse ? 2 : 1 }}>
        <div style={{ width: 40, height: 4, borderRadius: 4, background: accent, marginBottom: 14 }} />
        <div style={{ fontSize: 21, fontWeight: 700 }}>{title}</div>
        <p style={{ fontSize: 15, color: MUT, lineHeight: 1.6, marginTop: 8 }}>{text}</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginTop: 14 }}>
          {bullets.map(b => <div key={b} style={{ display: 'flex', gap: 9, fontSize: 14, alignItems: 'center' }}><Tick small color={accent} /> <span>{b}</span></div>)}
        </div>
      </div>
      <div style={{ order: reverse ? 1 : 2 }}>{visual}</div>
    </div>
  );
}

// ---- Mock visuals ----
function HeroMock() {
  const bars = [42, 61, 50, 78, 68, 92];
  return (
    <div className="fb-float" style={{ background: '#fff', borderRadius: 18, boxShadow: '0 30px 60px -30px rgba(0,0,0,0.5)', padding: 18, color: INK }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 13, color: MUT, fontWeight: 600 }}>This month</div>
        <div style={{ fontSize: 11, color: TEAL, background: MIST, padding: '3px 8px', borderRadius: 20, fontWeight: 700 }}>▲ 18%</div>
      </div>
      <div style={{ fontSize: 30, fontWeight: 800, marginTop: 8, letterSpacing: '-0.02em' }}>$48,250</div>
      <div style={{ fontSize: 13, color: MUT }}>Net profit</div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', height: 90, marginTop: 16 }}>
        {bars.map((h, i) => <div key={i} style={{ flex: 1, height: h + '%', background: i === bars.length - 1 ? GREEN : (i % 2 ? TEAL : MINT), borderRadius: '6px 6px 0 0' }} />)}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16, borderTop: '1px solid #eef1f0', paddingTop: 14 }}>
        {[['Invoices', '128'], ['Collected', '$41k'], ['Outstanding', '$7.2k']].map(([l, v]) => <div key={l}><div style={{ fontSize: 11, color: MUT }}>{l}</div><div style={{ fontSize: 15, fontWeight: 700 }}>{v}</div></div>)}
      </div>
    </div>
  );
}
// Generic invoice mock driven by an edition example + accent
function InvoiceMock({ accent, ex }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e9ecec', borderRadius: 14, padding: 18, boxShadow: '0 16px 40px -28px rgba(10,61,46,0.4)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `2px solid ${accent}`, paddingBottom: 10 }}>
        <span style={{ fontFamily: 'Georgia, serif', color: accent, fontWeight: 700 }}>FinlyBooks</span>
        <span style={{ fontSize: 13, fontWeight: 700 }}>INVOICE</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12, fontSize: 12, color: MUT }}><span>Bill to: {ex.billTo}</span><span>INV-0001</span></div>
      <div style={{ background: accent + '12', borderRadius: 8, padding: 10, marginTop: 10, fontSize: 12, color: accent, fontWeight: 500 }}>{ex.strip}</div>
      {ex.lines.map(([l, v]) => <div key={l} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginTop: 8 }}><span style={{ color: MUT }}>{l}</span><span>{v}</span></div>)}
      <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #eef1f0', marginTop: 10, paddingTop: 10, fontWeight: 800 }}><span>Net total</span><span style={{ color: accent }}>{ex.net}</span></div>
    </div>
  );
}
function ChartMock() {
  const pts = [30, 45, 40, 62, 58, 80, 92];
  const w = 280, h = 120, max = 100;
  const path = pts.map((p, i) => `${(i / (pts.length - 1)) * w},${h - (p / max) * h}`).join(' ');
  return (
    <div style={{ background: '#fff', border: '1px solid #e9ecec', borderRadius: 14, padding: 18, boxShadow: '0 16px 40px -28px rgba(37,99,235,0.35)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}><span style={{ fontWeight: 700 }}>Profit &amp; Loss</span><span style={{ color: BLUE, fontWeight: 700 }}>+22%</span></div>
      <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', height: 120, marginTop: 10 }} preserveAspectRatio="none">
        <polyline points={path} fill="none" stroke={BLUE} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        <polyline points={`0,${h} ${path} ${w},${h}`} fill={BLUE} opacity="0.08" />
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
        {[['Income', '$62k'], ['Expenses', '$41k'], ['Profit', '$21k']].map(([l, v]) => <div key={l}><div style={{ fontSize: 11, color: MUT }}>{l}</div><div style={{ fontSize: 15, fontWeight: 700 }}>{v}</div></div>)}
      </div>
    </div>
  );
}
function PayMock() {
  return (
    <div style={{ background: '#fff', border: '1px solid #e9ecec', borderRadius: 14, padding: 18, boxShadow: '0 16px 40px -28px rgba(124,58,237,0.35)' }}>
      <div style={{ fontSize: 13, fontWeight: 700 }}>Receipt · settle bills</div>
      {[['INV-0001', '$558', 'paid', GREEN], ['INV-0002', '$300', 'partial', BLUE], ['INV-0003', '$120', 'unpaid', '#854f0b']].map(([n, a, s, c]) => (
        <div key={n} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fafbfb', borderRadius: 8, padding: '10px 12px', marginTop: 8 }}>
          <span style={{ fontSize: 13, color: MUT }}>{n}</span><span style={{ fontSize: 13, fontWeight: 600 }}>{a}</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: c, background: c + '18', padding: '3px 8px', borderRadius: 12 }}>{s}</span>
        </div>
      ))}
    </div>
  );
}

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: '1px solid #e9ecec' }}>
      <button onClick={() => setOpen(o => !o)} style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0', textAlign: 'left' }}>
        <span style={{ fontSize: 16, fontWeight: 600, color: INK }}>{q}</span>
        <span style={{ fontSize: 22, color: GREEN, lineHeight: 1, transform: open ? 'rotate(45deg)' : 'none', transition: 'transform .2s' }}>+</span>
      </button>
      {open && <div style={{ fontSize: 14.5, color: MUT, lineHeight: 1.6, padding: '0 0 18px' }}>{a}</div>}
    </div>
  );
}

function IndustryPage({ cfg, onGetStarted, go }) {
  return (
    <>
      <div style={{ background: `radial-gradient(1000px 400px at 75% -20%, ${cfg.accent} 0%, ${cfg.accent}cc 60%)`, color: '#fff', padding: '48px clamp(16px,5vw,40px) 44px' }}>
        <div style={{ maxWidth: 1080, margin: '0 auto' }}>
          <button onClick={() => go('home')} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.85)', cursor: 'pointer', fontSize: 14, marginBottom: 18 }}>← Back to home</button>
          <div style={{ maxWidth: 720 }}>
            <span style={{ display: 'inline-block', background: 'rgba(255,255,255,0.18)', color: '#fff', fontSize: 13, fontWeight: 700, padding: '6px 14px', borderRadius: 20 }}>{cfg.icon} {cfg.label} edition</span>
            <h1 style={{ fontSize: 'clamp(28px,4.5vw,42px)', lineHeight: 1.15, fontWeight: 800, margin: '16px 0 0', letterSpacing: '-0.02em' }}>{cfg.heroTitle}</h1>
            <p style={{ fontSize: 18, color: 'rgba(255,255,255,0.92)', lineHeight: 1.6, margin: '16px 0 0' }}>{cfg.heroSub}</p>
            <div style={{ marginTop: 26 }}><button onClick={onGetStarted} className="fb-lift" style={{ background: '#fff', color: cfg.accent, border: 'none', padding: '14px 28px', borderRadius: 10, cursor: 'pointer', fontSize: 16, fontWeight: 700 }}>Start free →</button></div>
          </div>
        </div>
      </div>
      <Section>
        <div className="fb-frow" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 30, alignItems: 'center' }}>
          <div>
            <Kicker>Example invoice</Kicker>
            <h2 style={h2}>See a {cfg.short.toLowerCase()} invoice</h2>
            <p style={{ fontSize: 15, color: MUT, lineHeight: 1.6, marginTop: 10 }}>{cfg.blurb}</p>
          </div>
          <InvoiceMock accent={cfg.accent} ex={cfg.example} />
        </div>
      </Section>
      <Section bg={cfg.soft}>
        <Kicker>What you get</Kicker>
        <h2 style={h2}>Built around how you work</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: 22, marginTop: 28 }}>
          {cfg.benefits.map(([t, d]) => (
            <div key={t} className="fb-card" style={{ background: '#fff', border: '1px solid #e9ecec', borderRadius: 14, padding: 24 }}>
              <div style={{ width: 36, height: 36, borderRadius: 9, background: cfg.accent + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', color: cfg.accent }}><Tick color={cfg.accent} /></div>
              <div style={{ fontWeight: 700, fontSize: 16, marginTop: 14 }}>{t}</div>
              <div style={{ fontSize: 14, color: MUT, lineHeight: 1.55, marginTop: 6 }}>{d}</div>
            </div>
          ))}
        </div>
      </Section>
      <CTABand onGetStarted={onGetStarted} accent={cfg.accent} />
    </>
  );
}

function Pricing({ onGetStarted }) {
  const tiers = [
    { name: 'Free', price: '$0', per: 'always', sub: 'No card needed', tagline: 'Everything to keep real books for one small business.', cta: 'Start free', highlight: false,
      feats: ['1 user, 1 company', 'Up to 100 invoices / month', 'Double-entry ledgers & trial balance', 'P&L, balance sheet & aging', 'Receipts, payments & bill-to-bill', 'Free no-login invoice tools'] },
    { name: 'Pro', price: '$8', per: 'per month', sub: 'or $80/year — save 2 months', tagline: 'For growing businesses that need power and polish.', cta: 'Start 14-day trial', highlight: true,
      feats: ['Everything in Free', 'Unlimited invoices', 'Multi-currency & forex gain/loss', 'Tax / VAT modules', 'DSR bulk upload', 'Custom logo (remove branding)', 'Up to 5 users', 'Backup & Tally / ERP export', 'Priority support'] },
    { name: 'Enterprise', price: 'Custom', per: 'contact sales', sub: 'For agencies & accountants', tagline: 'For accountants, agencies and multi-company operations.', cta: 'Contact sales', highlight: false,
      feats: ['Everything in Pro', 'Multiple companies', 'Unlimited team seats', 'White-label option', 'Client / agency dashboards', 'Dedicated onboarding', 'Priority + SLA support'] },
  ];
  return (
    <>
      <Section pad="56px clamp(16px,5vw,40px) 20px">
        <div style={{ textAlign: 'center', maxWidth: 640, margin: '0 auto' }}>
          <Kicker center>Pricing</Kicker>
          <h1 style={{ fontSize: 'clamp(28px,4.5vw,40px)', fontWeight: 800, letterSpacing: '-0.02em', margin: '8px 0 0' }}>Start free. Upgrade when you grow.</h1>
          <p style={{ fontSize: 17, color: MUT, marginTop: 12, lineHeight: 1.6 }}>A real free plan to get going, Pro when you need more power, and Enterprise for teams and agencies. Cancel anytime.</p>
        </div>
      </Section>
      <Section pad="10px clamp(16px,5vw,40px) 60px">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: 22, alignItems: 'start' }}>
          {tiers.map(t => (
            <div key={t.name} className="fb-card" style={{ background: '#fff', border: t.highlight ? `2px solid ${GREEN}` : '1px solid #e9ecec', borderRadius: 18, padding: 28, position: 'relative' }}>
              {t.highlight && <span style={{ position: 'absolute', top: -12, left: 28, background: GREEN, color: '#fff', fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 20 }}>Most popular</span>}
              <div style={{ fontWeight: 700, fontSize: 18 }}>{t.name}</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 8 }}><span style={{ fontSize: 34, fontWeight: 800, letterSpacing: '-0.02em' }}>{t.price}</span><span style={{ fontSize: 14, color: MUT }}>{t.per}</span></div>
              <div style={{ fontSize: 12.5, color: TEAL, fontWeight: 600, marginTop: 2 }}>{t.sub}</div>
              <div style={{ fontSize: 14, color: MUT, lineHeight: 1.5, marginTop: 10, minHeight: 42 }}>{t.tagline}</div>
              <button onClick={onGetStarted} className="fb-lift" style={{ ...(t.highlight ? btnPrimary : btnGhost), width: '100%', padding: '12px', marginTop: 16 }}>{t.cta}</button>
              <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>{t.feats.map(x => <div key={x} style={{ display: 'flex', gap: 9, fontSize: 14 }}><Tick small /> <span>{x}</span></div>)}</div>
            </div>
          ))}
        </div>
        <div style={{ textAlign: 'center', color: MUT, fontSize: 13, marginTop: 24, lineHeight: 1.6 }}>Prices are indicative and set for emerging markets — final pricing may vary by region and currency.<br/>All plans include automatic double-entry, secure cloud storage, and web + Android access.</div>
      </Section>
    </>
  );
}

function About({ onGetStarted }) {
  return (
    <>
      <Section pad="56px clamp(16px,5vw,40px) 30px">
        <div style={{ maxWidth: 760 }}>
          <Kicker>Our story</Kicker>
          <h1 style={{ fontSize: 'clamp(28px,4.5vw,40px)', fontWeight: 800, letterSpacing: '-0.02em', margin: '8px 0 0' }}>Built by a finance professional, for small business</h1>
          <p style={{ fontSize: 17, color: MUT, lineHeight: 1.7, marginTop: 16 }}>FinlyBooks was created by a finance and accounting professional with over a decade of multi-country experience across audit, travel, and corporate finance. After years of watching small businesses struggle with software that was too complex or too costly, the goal became simple: give every small business owner clear, real-time books — free, and genuinely easy to use.</p>
          <p style={{ fontSize: 17, color: MUT, lineHeight: 1.7, marginTop: 14 }}>We start where the big players don't: the shops, agencies and traders of emerging markets. Our ambition is to grow from simple books into a full financial platform for small business across MENA and beyond — the way Intuit, Zoho and Odoo did for larger firms.</p>
        </div>
      </Section>
      <Section bg="#fafbfb">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 20 }}>
          {[['Our mission', 'Give every small business a clear picture of their money — so they can track, manage and grow.', GREEN], ['Our approach', 'Hide the accounting complexity; keep proper double-entry running quietly underneath.', BLUE], ['Where we are going', 'From books to a financial platform: payments, tax and more, MENA-first.', VIOLET]].map(([t, d, c]) => (
            <div key={t} className="fb-card" style={{ background: '#fff', border: '1px solid #e9ecec', borderRadius: 14, padding: 22, borderTop: `3px solid ${c}` }}>
              <div style={{ fontWeight: 700, fontSize: 16 }}>{t}</div>
              <div style={{ fontSize: 14, color: MUT, lineHeight: 1.6, marginTop: 6 }}>{d}</div>
            </div>
          ))}
        </div>
      </Section>
      <Section>
        <div style={{ background: DARK, borderRadius: 18, padding: 'clamp(28px,5vw,44px)', color: '#fff', display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 24, alignItems: 'center' }}>
          <div>
            <h2 style={{ fontSize: 24, fontWeight: 800, margin: 0 }}>Get in touch</h2>
            <p style={{ color: MINT, marginTop: 8, lineHeight: 1.6, fontSize: 15 }}>Questions, partnerships or support — we would love to hear from you.</p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <a href="mailto:info@finlybooks.com" style={contactBtn}>Email: info@finlybooks.com</a>
            <a href="https://wa.me/919904156467" style={contactBtn}>WhatsApp: +91 99041 56467</a>
            <a href="https://www.finlybooks.com" style={contactBtn}>www.finlybooks.com</a>
          </div>
        </div>
      </Section>
    </>
  );
}

function CTABand({ onGetStarted, accent = GREEN }) {
  return (
    <Section pad="0 clamp(16px,5vw,40px) 72px">
      <div style={{ background: `radial-gradient(800px 300px at 80% 0%, ${TEAL} 0%, ${accent} 60%)`, borderRadius: 20, padding: 'clamp(32px,6vw,56px)', textAlign: 'center', color: '#fff' }}>
        <h2 style={{ fontSize: 'clamp(24px,4vw,34px)', fontWeight: 800, margin: 0, letterSpacing: '-0.02em' }}>Ready to take control of your books?</h2>
        <p style={{ fontSize: 17, opacity: 0.92, margin: '12px 0 0' }}>Join small businesses keeping simpler, smarter books — free.</p>
        <button onClick={onGetStarted} className="fb-lift" style={{ background: '#fff', color: accent, border: 'none', padding: '15px 36px', borderRadius: 10, cursor: 'pointer', fontSize: 16, fontWeight: 700, marginTop: 22 }}>Start free →</button>
      </div>
    </Section>
  );
}

function Footer({ go, onGetStarted }) {
  const col = (title, items) => (
    <div>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', marginBottom: 12 }}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>{items.map(([l, fn]) => <button key={l} onClick={fn} style={{ background: 'none', border: 'none', textAlign: 'left', color: MINT, cursor: 'pointer', fontSize: 14, padding: 0 }}>{l}</button>)}</div>
    </div>
  );
  return (
    <div style={{ background: DARK, color: '#fff', padding: '48px clamp(16px,5vw,40px) 28px' }}>
      <div style={{ maxWidth: 1080, margin: '0 auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr repeat(3,1fr)', gap: 28 }} className="fb-footcols">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <LogoIcon size={32} />
              <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1 }}>
                <span style={{ fontSize: 18, fontWeight: 700, fontFamily: 'Georgia, serif' }}>FinlyBooks</span>
                <span style={{ fontSize: 9, fontWeight: 600, color: MINT, letterSpacing: '0.06em', marginTop: 3 }}>TRACK. MANAGE. GROW.</span>
              </span>
            </div>
            <p style={{ color: MINT, fontSize: 14, lineHeight: 1.6, marginTop: 12, maxWidth: 260 }}>Free, simple accounting for the world's small businesses.</p>
          </div>
          {col('Product', [['Travel edition', () => go('tourism')], ['Service edition', () => go('service')], ['Business edition', () => go('general')], ['Pricing', () => go('pricing')]])}
          {col('Company', [['About', () => go('about')], ['Contact', () => go('about')], ['Get started', onGetStarted]])}
          {col('Contact', [['info@finlybooks.com', () => { window.location.href = 'mailto:info@finlybooks.com'; }], ['WhatsApp', () => { window.open('https://wa.me/919904156467'); }]])}
        </div>
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.12)', marginTop: 32, paddingTop: 20, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, color: '#7fbfa8', fontSize: 13 }}>
          <span>© 2026 FinlyBooks. All rights reserved.</span><span>Track. Manage. Grow.</span>
        </div>
      </div>
    </div>
  );
}

function Kicker({ children, center }) { return <div style={{ color: GREEN, fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.08em', textAlign: center ? 'center' : 'left' }}>{children}</div>; }
function Tick({ color = GREEN, small, muted }) { const s = small ? 14 : 16; return <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={muted ? '#9bb0a8' : color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>; }
function Dash() { return <span style={{ color: '#c7cfcb', fontSize: 18 }}>–</span>; }
function Dot() { return <span style={{ width: 7, height: 7, borderRadius: '50%', background: TEAL, display: 'inline-block' }} />; }
function StyleTag() {
  return <style>{`
    .fb-lift{transition:transform .12s, box-shadow .15s}
    .fb-lift:hover{transform:translateY(-2px); box-shadow:0 10px 24px -10px rgba(0,0,0,0.3)}
    .fb-card{transition:transform .15s, box-shadow .15s, border-color .15s}
    .fb-card:hover{transform:translateY(-3px); box-shadow:0 16px 36px -22px rgba(10,61,46,0.35); border-color:#cfe0d9}
    .fb-float{animation:fbfloat 5s ease-in-out infinite}
    @keyframes fbfloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}}
    @media (max-width:860px){ .fb-hero{grid-template-columns:1fr !important} .fb-frow{grid-template-columns:1fr !important} .fb-frow > div{order:initial !important} .fb-editionpanel{grid-template-columns:1fr !important} .fb-footcols{grid-template-columns:1fr 1fr !important} }
    @media (max-width:620px){ .fb-navlinks{display:none !important} .fb-footcols{grid-template-columns:1fr !important} .fb-float{animation:none} }
  `}</style>;
}
const h2 = { fontSize: 'clamp(22px,3.5vw,30px)', fontWeight: 800, letterSpacing: '-0.02em', margin: '8px 0 0' };
const btnPrimary = { background: GREEN, color: '#fff', border: 'none', padding: '9px 20px', borderRadius: 9, cursor: 'pointer', fontSize: 14, fontWeight: 600 };
const btnGhost = { background: '#fff', color: INK, border: '1px solid #d7dedb', padding: '9px 20px', borderRadius: 9, cursor: 'pointer', fontSize: 14, fontWeight: 600 };
const contactBtn = { background: 'rgba(255,255,255,0.1)', color: '#fff', textDecoration: 'none', padding: '11px 16px', borderRadius: 10, fontSize: 14, fontWeight: 500 };
const cmpTh = { padding: '12px 14px', fontSize: 13, color: MUT, textAlign: 'center', borderBottom: '2px solid #e9ecec' };
const cmpTd = { padding: '12px 14px', fontSize: 14, textAlign: 'center', borderBottom: '1px solid #f0f2f1' };
